import React, { useEffect, useState, useCallback, useRef } from "react";

const TransitionOverlay = ({ active, onComplete }) => {
  const [phase, setPhase] = useState(0);
  const completeCalled = useRef(false);

  const handleComplete = useCallback(() => {
    if (!completeCalled.current) {
      completeCalled.current = true;
      onComplete();
    }
  }, [onComplete]);

  useEffect(() => {
    if (!active) return;
    completeCalled.current = false;

    const timers = [];
    const t = (fn, ms) => timers.push(setTimeout(fn, ms));

    t(() => setPhase(1), 50);      // overlay fades in
    t(() => setPhase(2), 500);     // ring expands + letters start
    t(() => setPhase(3), 1400);    // gold line draws + tagline
    t(() => setPhase(4), 2800);    // everything fades out
    t(() => handleComplete(), 3500); // navigate

    return () => timers.forEach(clearTimeout);
  }, [active, handleComplete]);

  if (!active && phase === 0) return null;

  const letters = "AWARE".split("");

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Cormorant:wght@300;400;500&family=Inter:wght@300;400;500&display=swap');

    .tr-overlay {
      position: fixed;
      inset: 0;
      z-index: 99999;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background: #074F4F;
      opacity: 0;
      transition: opacity 0.5s cubic-bezier(0.16, 1, 0.3, 1);
      overflow: hidden;
    }
    .tr-overlay.phase1,
    .tr-overlay.phase2,
    .tr-overlay.phase3 {
      opacity: 1;
    }
    .tr-overlay.phase4 {
      opacity: 0;
      transition: opacity 0.7s cubic-bezier(0.7, 0, 0.3, 1);
    }

    /* ── Ambient particles ── */
    .tr-particles {
      position: absolute;
      inset: 0;
      overflow: hidden;
      pointer-events: none;
    }
    .tr-particle {
      position: absolute;
      border-radius: 50%;
      background: radial-gradient(circle, rgba(198,167,105,0.15) 0%, transparent 70%);
      animation: tr-float 6s ease-in-out infinite;
    }
    .tr-particle:nth-child(1) { width: 300px; height: 300px; top: -10%; left: -5%; animation-delay: 0s; }
    .tr-particle:nth-child(2) { width: 200px; height: 200px; bottom: -8%; right: -3%; animation-delay: -2s; }
    .tr-particle:nth-child(3) { width: 150px; height: 150px; top: 30%; right: 10%; animation-delay: -4s; }
    .tr-particle:nth-child(4) { width: 100px; height: 100px; bottom: 20%; left: 15%; animation-delay: -1s; }

    @keyframes tr-float {
      0%, 100% { transform: translateY(0) scale(1); opacity: 0.4; }
      50% { transform: translateY(-20px) scale(1.1); opacity: 0.7; }
    }

    /* ── Golden ring ── */
    .tr-ring {
      position: absolute;
      width: 120px;
      height: 120px;
      border: 1.5px solid rgba(198,167,105,0.3);
      border-radius: 50%;
      transform: scale(0);
      opacity: 0;
      transition: all 1.2s cubic-bezier(0.16, 1, 0.3, 1);
    }
    .tr-overlay.phase2 .tr-ring,
    .tr-overlay.phase3 .tr-ring {
      transform: scale(1);
      opacity: 1;
    }
    .tr-ring-inner {
      position: absolute;
      width: 180px;
      height: 180px;
      border: 1px solid rgba(198,167,105,0.12);
      border-radius: 50%;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%) scale(0);
      opacity: 0;
      transition: all 1.4s cubic-bezier(0.16, 1, 0.3, 1) 0.2s;
    }
    .tr-overlay.phase2 .tr-ring-inner,
    .tr-overlay.phase3 .tr-ring-inner {
      transform: translate(-50%, -50%) scale(1);
      opacity: 1;
    }

    /* ── Text container ── */
    .tr-text {
      position: relative;
      z-index: 2;
      text-align: center;
    }

    /* ── Letter-by-letter reveal ── */
    .tr-letters {
      display: flex;
      justify-content: center;
      gap: 12px;
      margin-bottom: 0;
    }
    .tr-letter {
      font-family: 'Cormorant', Georgia, serif;
      font-size: clamp(48px, 8vw, 80px);
      font-weight: 300;
      letter-spacing: 12px;
      color: #FAF7F2;
      opacity: 0;
      transform: translateY(30px);
      transition: all 0.7s cubic-bezier(0.16, 1, 0.3, 1);
      position: relative;
    }
    .tr-letter.visible {
      opacity: 1;
      transform: translateY(0);
    }

    /* Gold shimmer on letters */
    .tr-letter::after {
      content: attr(data-letter);
      position: absolute;
      inset: 0;
      color: #C6A769;
      opacity: 0;
      animation: tr-shimmer 2s ease-in-out infinite;
    }
    .tr-letter.visible::after {
      animation-delay: calc(var(--i) * 0.15s);
    }

    @keyframes tr-shimmer {
      0%, 100% { opacity: 0; }
      50% { opacity: 0.4; }
    }

    /* ── Gold underline ── */
    .tr-line {
      width: 80px;
      height: 1.5px;
      background: linear-gradient(90deg, transparent, #C6A769, transparent);
      margin: 20px auto 0;
      transform: scaleX(0);
      transition: transform 1s cubic-bezier(0.16, 1, 0.3, 1);
    }
    .tr-overlay.phase3 .tr-line {
      transform: scaleX(1);
    }

    /* ── Tagline ── */
    .tr-tagline {
      font-family: 'Inter', sans-serif;
      font-size: 11px;
      font-weight: 300;
      letter-spacing: 4px;
      text-transform: uppercase;
      color: rgba(250,247,242,0.4);
      margin-top: 24px;
      opacity: 0;
      transform: translateY(10px);
      transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.2s;
    }
    .tr-overlay.phase3 .tr-tagline {
      opacity: 1;
      transform: translateY(0);
    }

    /* ── Grain ── */
    .tr-grain {
      position: absolute;
      inset: 0;
      pointer-events: none;
      opacity: 0.03;
      background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
      background-size: 128px;
    }

    /* ── Horizontal light beam ── */
    .tr-beam {
      position: absolute;
      width: 100%;
      height: 1px;
      top: 50%;
      background: linear-gradient(90deg, transparent 0%, rgba(198,167,105,0.08) 20%, rgba(198,167,105,0.15) 50%, rgba(198,167,105,0.08) 80%, transparent 100%);
      transform: scaleX(0);
      transition: transform 1.5s cubic-bezier(0.16, 1, 0.3, 1) 0.3s;
    }
    .tr-overlay.phase2 .tr-beam,
    .tr-overlay.phase3 .tr-beam {
      transform: scaleX(1);
    }
  `;

  return (
    <div className={`tr-overlay ${phase >= 1 ? "phase1" : ""} ${phase >= 2 ? "phase2" : ""} ${phase >= 3 ? "phase3" : ""} ${phase >= 4 ? "phase4" : ""}`}>
      <style>{css}</style>
      <div className="tr-grain" />
      <div className="tr-particles">
        <div className="tr-particle" />
        <div className="tr-particle" />
        <div className="tr-particle" />
        <div className="tr-particle" />
      </div>
      <div className="tr-beam" />
      <div className="tr-ring">
        <div className="tr-ring-inner" />
      </div>
      <div className="tr-text">
        <div className="tr-letters">
          {letters.map((letter, i) => (
            <span
              key={i}
              className={`tr-letter ${phase >= 2 ? "visible" : ""}`}
              data-letter={letter}
              style={{
                "--i": i,
                transitionDelay: phase >= 2 ? `${i * 0.12}s` : "0s",
              }}
            >
              {letter}
            </span>
          ))}
        </div>
        <div className="tr-line" />
        <div className="tr-tagline">Spațiul tău sigur</div>
      </div>
    </div>
  );
};

export default TransitionOverlay;
